import { useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ASSET_CATEGORIES, EXPIRY_WARN_DAYS, daysUntil, type Asset, type AssetCategory } from '@dxtech/shared';
import { call } from '../api/library.js';
import { useApp } from '../state/app.js';
import { ModalFrame, Spinner } from '../ui/common.js';

// 장비·자산·라이선스 관리 — 관리자: 전체 등록·수정·지급/회수·삭제, 그 외: 내가 쓰는 장비만 조회
interface Person { upn: string; name: string; dept: string; title: string }
export const fetchAssets = async () => (await (await call('/assets')).json()) as { items: Asset[]; canManage: boolean };
const today = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
const dot = (d: string) => d.replace(/-/g, '.');

function Expiry({ date }: { date: string }): JSX.Element {
  const left = daysUntil(date, today());
  if (left == null) return <span style={{ color: '#D1D5DB' }}>—</span>;
  const color = left < 0 ? '#B91C1C' : left <= EXPIRY_WARN_DAYS ? '#B45309' : '#4B5563';
  return (
    <span className="tnum" style={{ color }}>
      {dot(date)}
      {left <= EXPIRY_WARN_DAYS && <span style={{ marginLeft: 6, fontSize: 11.5, fontWeight: 700 }}>{left < 0 ? `만료 ${-left}일 지남` : left === 0 ? '오늘 만료' : `D-${left}`}</span>}
    </span>
  );
}

const GRID = '120px 1.4fr 1fr 110px 150px 1fr 150px';

export function Assets(): JSX.Element {
  const q = useQuery({ queryKey: ['assets'], queryFn: fetchAssets });
  const [cat, setCat] = useState<'' | AssetCategory>('');
  const [kw, setKw] = useState('');
  const [onlyWarn, setOnlyWarn] = useState(false);
  const [editing, setEditing] = useState<Asset | 'new' | null>(null);
  const [assigning, setAssigning] = useState<Asset | null>(null);
  const [historyOf, setHistoryOf] = useState<Asset | null>(null);

  const all = q.data?.items ?? [];
  const canManage = q.data?.canManage ?? false;
  const t = today();
  const warn = all.filter((a) => { const d = daysUntil(a.expiresAt, t); return d != null && d <= EXPIRY_WARN_DAYS; });
  const rows = useMemo(() => {
    const k = kw.trim().toLowerCase();
    return all
      .filter((a) => !cat || a.category === cat)
      .filter((a) => !onlyWarn || warn.includes(a))
      .filter((a) => !k || [a.name, a.ident, a.holderName, a.note].some((v) => v.toLowerCase().includes(k)))
      .sort((a, b) => a.category.localeCompare(b.category, 'ko') || a.name.localeCompare(b.name, 'ko'));
  }, [all, cat, kw, onlyWarn, warn]);

  if (q.isLoading) return <div style={{ padding: 60, textAlign: 'center' }}><Spinner /></div>;
  if (q.isError) return <div className="dx-card" style={{ padding: 30, color: '#B91C1C' }}>자산 목록을 불러오지 못했습니다.</div>;

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gap: 14, animation: 'dxfade .3s ease' }}>
      {canManage && warn.length > 0 && (
        <button onClick={() => setOnlyWarn((v) => !v)} className="dx-card" style={{ padding: '12px 16px', textAlign: 'left', border: '1px solid #FCD34D', background: '#FFFBEB', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, color: '#92400E' }}>
          ⚠ {EXPIRY_WARN_DAYS}일 안에 만료되거나 이미 만료된 항목이 <b>{warn.length}건</b> 있습니다 — {warn.slice(0, 4).map((a) => a.name).join(', ')}{warn.length > 4 ? ' 외' : ''}
          <span style={{ marginLeft: 8, textDecoration: 'underline' }}>{onlyWarn ? '전체 보기' : '이 항목만 보기'}</span>
        </button>
      )}
      <div className="dx-card" style={{ padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <select className="dx-select" value={cat} onChange={(e) => setCat(e.target.value as AssetCategory | '')}>
          <option value="">전체 분류</option>
          {ASSET_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <input className="dx-input" style={{ width: 240 }} placeholder="이름·관리번호·사용자·메모 검색" value={kw} onChange={(e) => setKw(e.target.value)} />
        {canManage && <button className="dx-btn dx-btn-primary dx-btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setEditing('new')}>신규 등록</button>}
      </div>

      <div className="dx-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px 11px', borderBottom: '1px solid #F3F4F6', fontSize: 15, fontWeight: 700 }}>
          {canManage ? '장비·자산·라이선스 목록' : '내가 사용 중인 장비·라이선스'} <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>(총 {rows.length}건)</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 900 }}>
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 10, padding: '9px 18px', background: '#F9FAFB', fontSize: 12, fontWeight: 600, color: '#6B7280' }}>
              <span>분류</span><span>이름</span><span>관리번호</span><span>사용자</span><span>만료일</span><span>메모</span><span />
            </div>
            {rows.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>{canManage ? '등록된 항목이 없습니다.' : '지급받은 장비가 없습니다.'}</div> : rows.map((a) => (
              <div key={a.id} style={{ display: 'grid', gridTemplateColumns: GRID, gap: 10, padding: '11px 18px', borderTop: '1px solid #F3F4F6', fontSize: 13, alignItems: 'center' }}>
                <span style={{ color: '#6B7280' }}>{a.category}</span>
                <span style={{ fontWeight: 600 }}>{a.name}{a.seats ? <span style={{ color: '#9CA3AF', fontWeight: 400 }}> · {a.seats}개</span> : null}</span>
                <span className="tnum" style={{ color: '#4B5563' }}>{a.ident || '—'}</span>
                <span>{a.holderName || <span style={{ color: '#9CA3AF' }}>미지급</span>}</span>
                <Expiry date={a.expiresAt} />
                <span style={{ color: '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={a.note}>{a.note}</span>
                <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  {canManage && <>
                    <button className="dx-btn dx-btn-soft dx-btn-sm" style={{ padding: '5px 9px' }} onClick={() => setAssigning(a)}>{a.holderUpn ? '변경' : '지급'}</button>
                    <button className="dx-btn dx-btn-ghost dx-btn-sm" style={{ padding: '5px 9px' }} onClick={() => setEditing(a)}>수정</button>
                  </>}
                  <button className="dx-btn dx-btn-ghost dx-btn-sm" style={{ padding: '5px 9px' }} onClick={() => setHistoryOf(a)}>이력</button>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>카드번호·라이선스 키·비밀번호 같은 비밀값은 입력하지 마세요(관리번호는 끝 4자리 정도만).</div>

      {editing && <EditModal item={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {assigning && <AssignModal item={assigning} onClose={() => setAssigning(null)} />}
      {historyOf && (
        <ModalFrame title={`${historyOf.name} 지급·회수 이력`} onClose={() => setHistoryOf(null)}>
          {historyOf.history.length === 0 ? <div style={{ color: '#9CA3AF', fontSize: 13 }}>이력이 없습니다.</div> : (
            <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
              {[...historyOf.history].reverse().map((h, i) => (
                <div key={i} style={{ display: 'flex', gap: 10 }}>
                  <span className="tnum" style={{ color: '#9CA3AF', width: 84 }}>{dot(h.at.slice(0, 10))}</span>
                  <b style={{ color: h.action === 'assign' ? '#1D4ED8' : '#6B7280', width: 34 }}>{h.action === 'assign' ? '지급' : '회수'}</b>
                  <span style={{ flex: 1 }}>{h.name}</span>
                  <span style={{ color: '#9CA3AF' }}>처리 {h.by}</span>
                </div>
              ))}
            </div>
          )}
        </ModalFrame>
      )}
    </div>
  );
}

function EditModal({ item, onClose }: { item: Asset | null; onClose: () => void }): JSX.Element {
  const { flash } = useApp();
  const qc = useQueryClient();
  const [f, setF] = useState({
    category: item?.category ?? ASSET_CATEGORIES[0], name: item?.name ?? '', ident: item?.ident ?? '',
    expiresAt: item?.expiresAt ?? '', seats: item?.seats ? String(item.seats) : '', note: item?.note ?? '',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const done = (m: string) => { flash(m); qc.invalidateQueries({ queryKey: ['assets'] }); onClose(); };
  const save = useMutation({
    mutationFn: () => call('/assets', { id: item?.id, ...f, seats: f.seats ? Number(f.seats) : undefined }),
    onSuccess: () => done(item ? '수정했습니다' : '등록했습니다'),
    onError: (e) => flash(e instanceof Error ? e.message : '저장 실패'),
  });
  const del = useMutation({
    mutationFn: () => call('/assets/delete', { id: item!.id }),
    onSuccess: () => done('삭제했습니다'),
    onError: (e) => flash(e instanceof Error ? e.message : '삭제 실패'),
  });
  const label = { display: 'grid', gap: 5, fontSize: 12.5, fontWeight: 600, color: '#374151' } as const;
  return (
    <ModalFrame title={item ? '자산 수정' : '자산 신규 등록'} onClose={onClose} footer={<>
      {item && <button className="dx-btn dx-btn-danger-outline dx-btn-sm" style={{ marginRight: 'auto' }} disabled={del.isPending}
        onClick={() => { if (confirm(`'${item.name}'을(를) 삭제할까요? 이력도 함께 지워집니다.`)) del.mutate(); }}>삭제</button>}
      <button className="dx-btn dx-btn-ghost dx-btn-sm" onClick={onClose}>취소</button>
      <button className="dx-btn dx-btn-primary dx-btn-sm" disabled={!f.name.trim() || save.isPending} onClick={() => save.mutate()}>저장</button>
    </>}>
      <div style={{ display: 'grid', gap: 12 }}>
        <label style={label}>분류<select className="dx-select" value={f.category} onChange={set('category')}>{ASSET_CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></label>
        <label style={label}>이름(모델·제품명)<input className="dx-input" maxLength={100} value={f.name} onChange={set('name')} placeholder="예) LG gram 16 / Automation Anywhere" /></label>
        <label style={label}>관리번호<input className="dx-input" maxLength={60} value={f.ident} onChange={set('ident')} placeholder="예) NB-2026-03, 시리얼 끝 4자리" /></label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10 }}>
          <label style={label}>만료일(라이선스·카드 유효기간)<input type="date" className="dx-input" value={f.expiresAt} onChange={set('expiresAt')} /></label>
          <label style={label}>수량<input type="number" min={0} className="dx-input" value={f.seats} onChange={set('seats')} placeholder="라이선스" /></label>
        </div>
        <label style={label}>메모<textarea className="dx-textarea" rows={3} maxLength={500} value={f.note} onChange={set('note')} placeholder="구입일·구입처·갱신 담당 등" /></label>
      </div>
    </ModalFrame>
  );
}

function AssignModal({ item, onClose }: { item: Asset; onClose: () => void }): JSX.Element {
  const { flash } = useApp();
  const qc = useQueryClient();
  const people = useQuery({ queryKey: ['kudos', 'people'], queryFn: async () => (await (await call('/kudos/people')).json()) as { items: Person[] }, staleTime: 600_000 });
  const [upn, setUpn] = useState(item.holderUpn);
  const m = useMutation({
    mutationFn: (to: string) => call('/assets/assign', { id: item.id, upn: to }),
    onSuccess: (_r, to) => { flash(to ? '지급 처리했습니다' : '회수 처리했습니다'); qc.invalidateQueries({ queryKey: ['assets'] }); onClose(); },
    onError: (e) => flash(e instanceof Error ? e.message : '처리 실패'),
  });
  return (
    <ModalFrame title={`${item.name} 지급·회수`} sub={item.holderName ? `현재 사용자: ${item.holderName}` : '현재 미지급'} onClose={onClose} footer={<>
      {item.holderUpn && <button className="dx-btn dx-btn-danger-outline dx-btn-sm" style={{ marginRight: 'auto' }} disabled={m.isPending} onClick={() => m.mutate('')}>회수</button>}
      <button className="dx-btn dx-btn-ghost dx-btn-sm" onClick={onClose}>취소</button>
      <button className="dx-btn dx-btn-primary dx-btn-sm" disabled={!upn || upn === item.holderUpn || m.isPending} onClick={() => m.mutate(upn)}>지급</button>
    </>}>
      <select className="dx-select" style={{ width: '100%' }} value={upn} onChange={(e) => setUpn(e.target.value)}>
        <option value="">사용자 선택</option>
        {(people.data?.items ?? []).map((p) => <option key={p.upn} value={p.upn}>{p.name} {p.title} · {p.dept}</option>)}
      </select>
    </ModalFrame>
  );
}
